import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { FF_SIGNIN_TURNSTILE } from '@/consts/featureFlags';
import { applyFeatureFlagOverride } from '@/hooks/useFeatureFlag';
import { I18N_KEYS } from '@/language/keys';
import {
  recordSignInTurnstileTelemetry,
  SignInTurnstileStage,
  SignInTurnstileTelemetryOutcome,
  SignInTurnstileUnavailableReason,
} from '@/services/auth/signInTurnstileTelemetry';
import { clearTurnstileToken, getTurnstileToken, storeTurnstileToken } from '@/services/auth/turnstile-token.service';
import { TextStdRg } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { FlexCol } from '@/UI/styles';
import {
  toBoundedTurnstileErrorCode,
  TurnstileWidget,
  type TurnstileWidgetHandle,
  TurnstileWidgetTheme,
  TurnstileWidgetAppearance,
  TurnstileWidgetSize,
} from './TurnstileWidget';

export enum AuthenticationTurnstileStatus {
  Pending = 'pending',
  Ready = 'ready',
  // The check can't run: the script failed to load, or the browser is unsupported.
  Unavailable = 'unavailable',
  // The signin-turnstile flag is off or no sitekey is configured (local development), so sign-in needs no token.
  Disabled = 'disabled',
}

export function isTurnstileBlockingSignIn(status: AuthenticationTurnstileStatus): boolean {
  return status === AuthenticationTurnstileStatus.Pending || status === AuthenticationTurnstileStatus.Unavailable;
}

export const AUTHENTICATION_TURNSTILE_SLOW_DELAY_MS = 60_000;

/**
 * Reads `signin-turnstile` with PostHog's `fresh` option, which skips values cached from an earlier visit: one may be a
 * `false` from before the flag was turned back on, which would hide the widget while the server enforces.
 */
function useFreshSignInTurnstileFlag(): boolean | undefined {
  const posthogClient = usePostHog();
  const [searchParams] = useSearchParams();
  const [isFlagOn, setIsFlagOn] = useState(() => posthogClient.isFeatureEnabled(FF_SIGNIN_TURNSTILE, { fresh: true }));
  useEffect(
    () =>
      posthogClient.onFeatureFlags(() =>
        setIsFlagOn(posthogClient.isFeatureEnabled(FF_SIGNIN_TURNSTILE, { fresh: true })),
      ),
    [posthogClient],
  );
  return applyFeatureFlagOverride(FF_SIGNIN_TURNSTILE, isFlagOn, searchParams.toString());
}

export interface AuthenticationTurnstileWidgetHandle {
  getToken: () => string | undefined;
  reset: () => void;
}

interface AuthenticationTurnstileWidgetProps {
  onStatusChange: (status: AuthenticationTurnstileStatus) => void;
  theme?: TurnstileWidgetTheme;
  size?: TurnstileWidgetSize;
}

export const AuthenticationTurnstileWidget = forwardRef<
  AuthenticationTurnstileWidgetHandle,
  AuthenticationTurnstileWidgetProps
>(function AuthenticationTurnstileWidget({ onStatusChange, theme, size }, ref) {
  const { t } = useTranslation();
  const primitiveRef = useRef<TurnstileWidgetHandle>(null);
  const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const isSignInTurnstileFlagOn = useFreshSignInTurnstileFlag();
  // An unresolved flag (flags still loading, PostHog blocked, or the flag missing) keeps the widget, since the server
  // may be enforcing.
  const isTurnstileEnabled = Boolean(sitekey) && isSignInTurnstileFlagOn !== false;
  const [status, setStatus] = useState<AuthenticationTurnstileStatus | undefined>(undefined);
  const [isWidgetRendered, setIsWidgetRendered] = useState(false);
  const [hasWidgetFailed, setHasWidgetFailed] = useState(false);
  const widgetFailureReasonRef = useRef<SignInTurnstileUnavailableReason | undefined>(undefined);
  const [isSlow, setIsSlow] = useState(false);
  const [isInteractive, setIsInteractive] = useState(false);
  const mountTimeMsRef = useRef(performance.now());
  const isScriptLoadedRef = useRef(false);
  const hasRenderedWidgetRef = useRef(false);
  const hasRecordedReadyRef = useRef(false);
  const hasRecordedUnavailableRef = useRef(false);
  const hasRecordedSlowRef = useRef(false);
  const lastProviderErrorCodeRef = useRef<string | undefined>(undefined);

  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  });

  const reportStatus = useCallback((nextStatus: AuthenticationTurnstileStatus) => {
    setStatus(nextStatus);
    onStatusChangeRef.current(nextStatus);
  }, []);

  const readElapsedMs = (): number => Math.round(performance.now() - mountTimeMsRef.current);

  const describeStage = useCallback((): SignInTurnstileStage => {
    if (!isScriptLoadedRef.current) return SignInTurnstileStage.ScriptNotLoaded;
    if (!hasRenderedWidgetRef.current) return SignInTurnstileStage.WidgetNotRendered;
    return SignInTurnstileStage.AwaitingWidgetToken;
  }, []);

  useEffect(() => {
    reportStatus(
      isTurnstileEnabled
        ? getTurnstileToken()
          ? AuthenticationTurnstileStatus.Ready
          : AuthenticationTurnstileStatus.Pending
        : AuthenticationTurnstileStatus.Disabled,
    );
  }, [isTurnstileEnabled, reportStatus]);

  // Waiting on the user to solve a visible challenge is not slow; leaving it restarts the wait.
  useEffect(() => {
    if (status !== AuthenticationTurnstileStatus.Pending || isInteractive) return;
    const timeoutId = setTimeout(() => {
      setIsSlow(true);
      if (hasRecordedSlowRef.current) return;
      hasRecordedSlowRef.current = true;
      recordSignInTurnstileTelemetry({
        outcome: SignInTurnstileTelemetryOutcome.Slow,
        turnstileStage: describeStage(),
        providerErrorCode: lastProviderErrorCodeRef.current,
      });
    }, AUTHENTICATION_TURNSTILE_SLOW_DELAY_MS);
    return () => {
      clearTimeout(timeoutId);
      setIsSlow(false);
    };
  }, [status, isInteractive, describeStage]);

  const reportUnavailable = useCallback(() => {
    if (!hasRecordedUnavailableRef.current) {
      hasRecordedUnavailableRef.current = true;
      recordSignInTurnstileTelemetry({
        outcome: SignInTurnstileTelemetryOutcome.Unavailable,
        unavailableReason: widgetFailureReasonRef.current,
        turnstileStage: describeStage(),
      });
    }
    reportStatus(AuthenticationTurnstileStatus.Unavailable);
  }, [describeStage, reportStatus]);

  const handleNoToken = useCallback(() => {
    clearTurnstileToken();
    setIsInteractive(false);
    // A widget that could not load never produces a token; only a reload recovers it.
    if (widgetFailureReasonRef.current !== undefined) {
      reportUnavailable();
      return;
    }
    reportStatus(AuthenticationTurnstileStatus.Pending);
  }, [reportStatus, reportUnavailable]);

  const reset = useCallback(() => {
    if (!isTurnstileEnabled) {
      reportStatus(AuthenticationTurnstileStatus.Disabled);
      return;
    }
    handleNoToken();
    if (widgetFailureReasonRef.current === undefined) primitiveRef.current?.reset();
  }, [isTurnstileEnabled, reportStatus, handleNoToken]);

  useImperativeHandle(ref, () => ({ getToken: getTurnstileToken, reset }), [reset]);

  const markWidgetFailed = (unavailableReason: SignInTurnstileUnavailableReason) => {
    widgetFailureReasonRef.current = unavailableReason;
    setHasWidgetFailed(true);
  };

  if (!isTurnstileEnabled) return null;

  const isWidgetLoading = !isWidgetRendered && !hasWidgetFailed;
  const width = size === TurnstileWidgetSize.Flexible ? '100%' : undefined;

  return (
    <FlexCol sx={{ alignItems: 'center', gap: 1, width }}>
      <Box sx={{ position: 'relative', width }}>
        <TurnstileWidget
          ref={primitiveRef}
          sitekey={sitekey}
          action="signin"
          appearance={TurnstileWidgetAppearance.Always}
          theme={theme}
          size={size}
          onScriptLoaded={(wasScriptAlreadyLoaded) => {
            isScriptLoadedRef.current = true;
            recordSignInTurnstileTelemetry({
              outcome: SignInTurnstileTelemetryOutcome.ScriptLoaded,
              scriptLoadDurationMs: readElapsedMs(),
              wasScriptAlreadyLoaded,
            });
          }}
          onBeforeInteractive={() => setIsInteractive(true)}
          onAfterInteractive={() => setIsInteractive(false)}
          onRendered={() => {
            hasRenderedWidgetRef.current = true;
            setIsWidgetRendered(true);
          }}
          onSuccess={(token) => {
            storeTurnstileToken(token);
            lastProviderErrorCodeRef.current = undefined;
            if (!hasRecordedReadyRef.current) {
              hasRecordedReadyRef.current = true;
              recordSignInTurnstileTelemetry({
                outcome: SignInTurnstileTelemetryOutcome.Ready,
                readyDurationMs: readElapsedMs(),
              });
            }
            reportStatus(AuthenticationTurnstileStatus.Ready);
          }}
          onError={(errorCode) => {
            lastProviderErrorCodeRef.current = toBoundedTurnstileErrorCode(errorCode);
            // Turnstile retries on its own (retry: 'auto'), so an error keeps sign-in waiting rather than failing it.
            handleNoToken();
          }}
          onUnavailable={() => {
            markWidgetFailed(SignInTurnstileUnavailableReason.WidgetUnavailable);
            if (!getTurnstileToken()) reportUnavailable();
          }}
          onExpire={handleNoToken}
          onTimeout={handleNoToken}
          onUnsupported={() => {
            clearTurnstileToken();
            markWidgetFailed(SignInTurnstileUnavailableReason.WidgetUnsupportedInBrowser);
            reportUnavailable();
          }}
        />
        {isWidgetLoading && (
          <Box
            data-testid="authentication-turnstile-loading"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <CircularProgress size={24} fplProps={{ size: 'md' }} />
          </Box>
        )}
      </Box>
      {status === AuthenticationTurnstileStatus.Pending && isSlow && (
        <TextStdRg sx={{ textAlign: 'center' }}>{t(I18N_KEYS.SIGN_IN_PAGE.TURNSTILE_SLOW)}</TextStdRg>
      )}
      {status === AuthenticationTurnstileStatus.Unavailable && (
        <TextStdRg role="alert" sx={{ textAlign: 'center' }}>
          {t(I18N_KEYS.SIGN_IN_PAGE.TURNSTILE_UNAVAILABLE)}
        </TextStdRg>
      )}
    </FlexCol>
  );
});
